import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAction } from '../components/toast'
import {
  Avatar,
  Badge,
  Card,
  Empty,
  Kpi,
  Loaded,
  Meter,
  Notice,
  Page,
  ReasonDialog,
  Table,
} from '../components/ui'
import { api, get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, dateTime, money, ms, relative, titleCase, tokens } from '../lib/format'
import { useResource } from '../lib/useResource'
import { statusTone } from './Users'

type Detail = {
  id: string
  email: string
  email_verified: boolean
  status: string
  created_at: string | null
  last_seen_at: string | null
  roles: { role: string; granted_by: string | null }[]
  profile: {
    display_name: string
    avatar_url: string | null
    locale: string
    units: string
    timezone: string
    theme_preference: string
    member_since: string | null
  }
  subscription: {
    tier: string
    plan: string
    store: string
    renews_at: string | null
    trial_ends_at: string | null
    cancelled_at: string | null
  }
  devices: {
    id: string
    kind: string
    label: string
    firmware: string
    battery: number
    last_seen_at: string | null
  }[]
  connections: {
    provider: string
    enabled: boolean
    expires_at: string | null
    last_sync_at: string | null
    last_error: string | null
  }[]
  emergency: {
    contact_count: number
    has_primary: boolean
    thresholds: Record<string, number | boolean> | null
    events: { id: string; reason: string; outcome: string; triggered_at: string | null }[]
  }
  ai: {
    budget_usd: number | null
    hard_stop: boolean | null
    lifetime_spend_usd: number
    request_count: number
  }
  consents: { purpose: string; version: string; granted: boolean; at: string | null }[]
}

type Health = {
  passport: {
    blood_type: string
    chronic_conditions: string[]
    medications: string[]
    allergies: string[]
  }
  daily_totals: {
    day: string
    steps: number
    strain: number
    recovery: number
    active_calories: number | null
    distance_km: number | null
    active_minutes: number | null
  }[]
}

/** One reading as the band recorded it. Every field is nullable: a sensor that
 *  was not run has no value, which is different from a value of zero. */
type VitalSample = {
  recorded_at: string | null
  source: string
  heart_rate: number | null
  spo2: number | null
  systolic: number | null
  diastolic: number | null
  skin_temp_c: number | null
  respiration_rate: number | null
  hrv_ms: number | null
  stress: number | null
  glucose_mmol: number | null
  steps: number | null
  sleep_minutes: number | null
  deep_sleep_minutes: number | null
  rhythm: string
}

type Vitals = {
  days: number
  count: number
  /** True when the row limit was hit — the view is a window, not the whole set. */
  truncated: boolean
  samples: VitalSample[]
}

type AiRequestRow = {
  id: string
  created_at: string | null
  feature: string
  task_class: string
  provider: string
  model_id: string
  runner_up_model_id: string | null
  redactions: number
  failed_over_from: string | null
  error: string | null
  input_tokens: number
  output_tokens: number
  cost_usd: number
  latency_ms: number
  prompt_available: boolean
  prompt_text?: string
}

type AuditRow = {
  id: string
  at: string
  actor_id: string | null
  action: string
  target_type: string
  reason: string
  ip: string
  request_id: string
}

const TABS = [
  'Profile',
  'Health',
  'Devices',
  'Emergency',
  'AI',
  'Subscription',
  'Audit',
] as const
type Tab = (typeof TABS)[number]

const ROLE_OPTIONS = ['admin', 'support', 'clinician', 'researcher', 'service', 'superadmin']

const EDITABLE_FIELDS: { key: string; label: string; hint?: string }[] = [
  { key: 'display_name', label: 'Display name' },
  { key: 'units', label: 'Units', hint: 'metric | imperial' },
  { key: 'locale', label: 'Locale' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'pronouns', label: 'Pronouns' },
  { key: 'gender_identity', label: 'Gender identity' },
  { key: 'theme_preference', label: 'Theme', hint: 'auto | dark | light' },
]

export function UserDetail() {
  const { userId = '' } = useParams()
  const { can } = useAuth()
  const run = useAction()
  const [tab, setTab] = useState<Tab>('Profile')

  const detail = useResource((signal) => get<Detail>(`/v1/admin/users/${userId}`, undefined, signal), [
    userId,
  ])

  return (
    <Page
      title={
        <span className="row">
          <Avatar
            name={detail.data?.profile.display_name || detail.data?.email || '?'}
            url={detail.data?.profile.avatar_url}
            large
          />
          <span>{detail.data?.profile.display_name || detail.data?.email || 'User'}</span>
        </span>
      }
      subtitle={
        <>
          <span className="mono">{userId}</span> · joined {relative(detail.data?.created_at)} · last
          seen {relative(detail.data?.last_seen_at)}
        </>
      }
      actions={<Link className="btn btn--sm btn--ghost" to="/users">← All users</Link>}
    >
      <Loaded resource={detail}>
        {(user) => (
          <>
            <div className="row row--wrap">
              <Badge tone={statusTone(user.status)}>{titleCase(user.status)}</Badge>
              {user.subscription.tier === 'pro' ? <Badge tone="lime">Pro</Badge> : <Badge>Free</Badge>}
              {user.roles.map((r) => (
                <Badge key={r.role} tone={r.role === 'superadmin' ? 'alert' : undefined}>
                  {titleCase(r.role)}
                </Badge>
              ))}
              {!user.email_verified && <Badge tone="orange">Email unverified</Badge>}
              <div className="spacer" />
              <StatusActions user={user} onDone={detail.reload} />
            </div>

            <div className="tabs">
              {TABS.map((name) => (
                <button
                  key={name}
                  className={name === tab ? 'tab tab--active' : 'tab'}
                  onClick={() => setTab(name)}
                >
                  {name}
                </button>
              ))}
            </div>

            {tab === 'Profile' && <ProfileTab user={user} onSaved={detail.reload} />}
            {tab === 'Health' && <HealthTab userId={userId} canRead={can(P.phiReadAny)} />}
            {tab === 'Devices' && <DevicesTab user={user} />}
            {tab === 'Emergency' && <EmergencyTab user={user} />}
            {tab === 'AI' && <AiTab user={user} onSaved={detail.reload} />}
            {tab === 'Subscription' && <SubscriptionTab user={user} />}
            {tab === 'Audit' && <AuditTab userId={userId} />}

            {tab === 'Profile' && can(P.roleWrite) && (
              <RolesCard user={user} onChanged={detail.reload} run={run} />
            )}
          </>
        )}
      </Loaded>
    </Page>
  )
}

// ── status actions ───────────────────────────────────────────────────────────

function StatusActions({ user, onDone }: { user: Detail; onDone: () => void }) {
  const { can } = useAuth()
  const run = useAction()
  const [dialog, setDialog] = useState<'suspend' | 'reinstate' | 'reset' | 'verify' | null>(null)
  // Shown once, then gone. Nothing stores the plaintext, so navigating away
  // before handing it over means issuing another.
  const [issued, setIssued] = useState<string | null>(null)
  /// True when the server mailed the credential itself, so there is
  /// nothing for the operator to copy.
  const [emailed, setEmailed] = useState(false)

  if (!can(P.userSuspend)) return null

  return (
    <>
      {can(P.userWriteAny) && (
        <button className="btn btn--sm" onClick={() => setDialog('reset')}>
          Reset password
        </button>
      )}

      {can(P.userWriteAny) && !user.email_verified && (
        <button className="btn btn--sm" onClick={() => setDialog('verify')}>
          Mark email verified
        </button>
      )}

      {dialog === 'verify' && (
        <ReasonDialog
          title="Mark this address as verified"
          description="Use this for an account you created, where the person never received a verification mail. Say how the address was confirmed — the record keeps your reason."
          confirmLabel="Mark verified"
          minLength={10}
          onClose={() => setDialog(null)}
          onConfirm={(reason) =>
            run(
              () => post(`/v1/admin/users/${user.id}/verify-email`, { reason }),
              'Email marked verified.',
              onDone,
            )
          }
        />
      )}

      {dialog === 'reset' && (
        <ReasonDialog
          title="Reset this password"
          description="Issues a new temporary password and signs the person out everywhere. They set their own on next sign-in. If this deployment can send mail the password goes straight to them; otherwise it is shown here once for you to hand over."
          confirmLabel="Reset password"
          onClose={() => setDialog(null)}
          onConfirm={(reason) =>
            run(
              async () => {
                // `emailed` matters: when SMTP is configured the server sends
                // the credential itself and returns an EMPTY password. Reading
                // only `temporary_password` left `issued` falsy, so the
                // "copy it now" panel never rendered and the operator was left
                // with a success toast and no idea what had happened.
                const result = await post<{
                  temporary_password: string
                  emailed: boolean
                }>(`/v1/admin/users/${user.id}/reset-password`, { reason })
                setIssued(result.temporary_password)
                setEmailed(result.emailed)
              },
              'Password reset.',
              onDone,
            )
          }
        />
      )}

      {emailed && !issued && (
        <div className="stack" style={{ gap: 6, marginTop: 8 }}>
          <Notice>
            Sent to the account holder's email. Nothing to hand over — the
            password never passes through this console.
          </Notice>
        </div>
      )}

      {issued && (
        <div className="stack" style={{ gap: 6, marginTop: 8 }}>
          <Notice tone="warn">
            Shown once. Copy it now — it cannot be recovered.
          </Notice>
          <code
            style={{
              fontSize: 15,
              padding: '8px 10px',
              borderRadius: 4,
              background: 'var(--surface-2, rgba(127,127,127,.12))',
              userSelect: 'all',
            }}
          >
            {issued}
          </code>
          <button className="btn btn--sm" onClick={() => setIssued(null)}>
            Done
          </button>
        </div>
      )}

      {user.status === 'suspended' ? (
        <button className="btn btn--sm" onClick={() => setDialog('reinstate')}>
          Reinstate account
        </button>
      ) : (
        <button className="btn btn--sm btn--danger" onClick={() => setDialog('suspend')}>
          Suspend account
        </button>
      )}

      {dialog === 'suspend' && (
        <ReasonDialog
          title="Suspend this account"
          description="The person is signed out and cannot log back in. Emergency dispatch stops working for them — say why this is the right call."
          confirmLabel="Suspend"
          danger
          onClose={() => setDialog(null)}
          onConfirm={(reason) =>
            run(
              () => post(`/v1/admin/users/${user.id}/suspend`, { reason }),
              'Account suspended.',
              onDone,
            )
          }
        />
      )}

      {dialog === 'reinstate' && (
        <ReasonDialog
          title="Reinstate this account"
          confirmLabel="Reinstate"
          onClose={() => setDialog(null)}
          onConfirm={(reason) =>
            run(
              () => post(`/v1/admin/users/${user.id}/reinstate`, { reason }),
              'Account reinstated.',
              onDone,
            )
          }
        />
      )}
    </>
  )
}

// ── profile ──────────────────────────────────────────────────────────────────

function ProfileTab({ user, onSaved }: { user: Detail; onSaved: () => void }) {
  const { can } = useAuth()
  const run = useAction()
  const editable = can(P.userWriteAny)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [confirming, setConfirming] = useState(false)

  const value = (key: string) =>
    draft[key] ?? String((user.profile as unknown as Record<string, unknown>)[key] ?? '')

  const changed = Object.entries(draft).filter(
    ([key, next]) => next !== String((user.profile as unknown as Record<string, unknown>)[key] ?? ''),
  )

  return (
    <>
      <div className="grid grid--2">
        <Card title="Identity" hint="Editable from the console">
          <div className="stack">
            {EDITABLE_FIELDS.map((field) => (
              <div className="field" key={field.key}>
                <label className="field__label" htmlFor={field.key}>
                  {field.label}
                  {field.hint && <span className="dim"> · {field.hint}</span>}
                </label>
                <input
                  id={field.key}
                  className="input"
                  disabled={!editable}
                  value={value(field.key)}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                />
              </div>
            ))}
            <div className="row row--end">
              <button
                className="btn btn--primary btn--sm"
                disabled={!editable || changed.length === 0}
                onClick={() => setConfirming(true)}
              >
                Save {changed.length > 0 ? `(${changed.length})` : ''}
              </button>
            </div>
          </div>
        </Card>

        <div className="stack">
          <Card title="Account" hint="Read-only">
            <dl className="stack" style={{ margin: 0, gap: 10 }}>
              <Row label="Email" value={<span className="mono">{user.email}</span>} />
              <Row label="Verified" value={user.email_verified ? 'Yes' : 'No'} />
              <Row label="Member since" value={dateTime(user.profile.member_since)} />
              <Row label="Created" value={dateTime(user.created_at)} />
              <Row label="Last seen" value={dateTime(user.last_seen_at)} />
            </dl>
          </Card>

          <Card
            title="Avatar"
            hint="Uploaded by the person from the app; the console does not replace it"
          >
            <div className="row">
              <Avatar
                name={user.profile.display_name || user.email}
                url={user.profile.avatar_url}
                large
              />
              <span className="dim" style={{ fontSize: 11.5 }}>
                {user.profile.avatar_url
                  ? 'Custom image on file.'
                  : 'No image uploaded — initials are generated.'}
              </span>
            </div>
          </Card>

          <Card title="Consents" hint="What this person has agreed to, and when" flush>
            <Table
              rows={user.consents}
              rowKey={(row) => `${row.purpose}-${row.version}-${row.at}`}
              empty="No consent records."
              columns={[
                { key: 'purpose', header: 'Purpose', strong: true, render: (r) => titleCase(r.purpose) },
                { key: 'version', header: 'Version', render: (r) => <span className="mono">{r.version}</span> },
                {
                  key: 'granted',
                  header: 'State',
                  render: (r) =>
                    r.granted ? <Badge tone="green">Granted</Badge> : <Badge tone="orange">Declined</Badge>,
                },
                { key: 'at', header: 'When', render: (r) => <span className="dim">{dateTime(r.at)}</span> },
              ]}
            />
          </Card>
        </div>
      </div>

      {confirming && (
        <ReasonDialog
          title="Save profile changes"
          description={
            <>
              Changing: <span className="mono">{changed.map(([k]) => k).join(', ')}</span>. The
              before/after diff is written to the audit log.
            </>
          }
          confirmLabel="Save"
          onClose={() => setConfirming(false)}
          onConfirm={(reason) =>
            run(
              () =>
                api(`/v1/admin/users/${user.id}/profile`, {
                  method: 'PATCH',
                  body: { changes: Object.fromEntries(changed), reason },
                }),
              'Profile updated.',
              () => {
                setDraft({})
                onSaved()
              },
            )
          }
        />
      )}
    </>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="row" style={{ alignItems: 'baseline' }}>
      <dt className="dim" style={{ width: 130, flex: 'none', fontSize: 11.5 }}>
        {label}
      </dt>
      <dd style={{ margin: 0 }}>{value}</dd>
    </div>
  )
}

// ── health (PHI) ─────────────────────────────────────────────────────────────

function HealthTab({ userId, canRead }: { userId: string; canRead: boolean }) {
  const [reason, setReason] = useState('')
  const [opened, setOpened] = useState<string | null>(null)

  const health = useResource(
    (signal) =>
      opened
        ? get<Health>(`/v1/admin/users/${userId}/health`, { reason: opened }, signal)
        : Promise.resolve(null as unknown as Health),
    [userId, opened],
  )

  // The individual readings behind those totals. Same typed reason, same audit
  // entry — this is the data the console could not reach at all before, so a
  // person reporting palpitations showed a step count and nothing else.
  const vitals = useResource(
    (signal) =>
      opened
        ? get<Vitals>(
            `/v1/admin/users/${userId}/vitals`,
            { reason: opened, days: 7 },
            signal,
          )
        : Promise.resolve(null as unknown as Vitals),
    [userId, opened],
  )

  if (!canRead) {
    return (
      <Card>
        <Empty>
          Reading someone else's health record needs <span className="mono">phi:read:any</span>.
          Your roles do not carry it. This is by design — support and admin roles deliberately
          exclude PHI.
        </Empty>
      </Card>
    )
  }

  if (!opened) {
    return (
      <Card title="Open the health record">
        <div className="stack">
          <Notice tone="phi">
            You are about to open <strong>protected health information</strong>. The access is
            written to the audit log with your name and this reason <em>before</em> the data is
            returned — the record exists even if you close the tab.
          </Notice>
          <div className="field">
            <label className="field__label" htmlFor="phi-reason">
              Why you need to see this record (minimum 10 characters)
            </label>
            <textarea
              id="phi-reason"
              className="textarea"
              style={{ minHeight: 84 }}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Ticket 4821 — patient asked us to verify their allergy list after a sync failure"
            />
          </div>
          <div className="row row--end">
            <button
              className="btn btn--danger"
              disabled={reason.trim().length < 10}
              onClick={() => setOpened(reason.trim())}
            >
              Open record and log the access
            </button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="stack">
      <Notice tone="phi">
        <strong>You are viewing PHI.</strong> Logged reason: “{opened}”.{' '}
        <button className="btn btn--sm btn--ghost" onClick={() => setOpened(null)}>
          Close record
        </button>
      </Notice>
      <Loaded resource={health}>
        {(data) => (
          <div className="grid grid--2">
            <Card title="Medical passport" hint="What a paramedic reads off the NFC tag">
              <dl className="stack" style={{ margin: 0, gap: 10 }}>
                <Row label="Blood type" value={data.passport.blood_type || '—'} />
                <Row
                  label="Conditions"
                  value={data.passport.chronic_conditions.join(', ') || '—'}
                />
                <Row label="Medications" value={data.passport.medications.join(', ') || '—'} />
                <Row label="Allergies" value={data.passport.allergies.join(', ') || '—'} />
              </dl>
            </Card>
            <Card title="Daily totals · last 30 days" flush>
              <Table
                rows={data.daily_totals}
                rowKey={(row) => row.day}
                empty="No daily rollups recorded."
                columns={[
                  { key: 'day', header: 'Day', strong: true, render: (r) => r.day },
                  { key: 'steps', header: 'Steps', render: (r) => count(r.steps) },
                  {
                    key: 'cal',
                    header: 'Active kcal',
                    render: (r) => (r.active_calories == null ? '—' : count(r.active_calories)),
                  },
                  {
                    key: 'km',
                    header: 'Distance',
                    render: (r) => (r.distance_km == null ? '—' : `${r.distance_km.toFixed(2)} km`),
                  },
                  { key: 'strain', header: 'Strain', render: (r) => r.strain.toFixed(1) },
                  { key: 'recovery', header: 'Recovery', render: (r) => `${r.recovery}%` },
                ]}
              />
            </Card>
          </div>
        )}
      </Loaded>

      <Loaded resource={vitals}>
        {(data) => (
          <Card
            title={`Readings · last ${data.days} days`}
            hint={
              data.truncated
                ? `Showing the most recent ${data.count} — narrow the range to see the rest.`
                : `${data.count} reading${data.count === 1 ? '' : 's'} from the band.`
            }
            flush
          >
            <Table
              rows={data.samples}
              rowKey={(r) => `${r.recorded_at}-${r.source}`}
              empty="No individual readings recorded in this window."
              columns={[
                {
                  key: 'at',
                  header: 'Recorded',
                  strong: true,
                  render: (r) => (r.recorded_at ? dateTime(r.recorded_at) : '—'),
                },
                { key: 'hr', header: 'HR', render: (r) => dash(r.heart_rate, 'bpm') },
                { key: 'spo2', header: 'SpO₂', render: (r) => dash(r.spo2, '%') },
                {
                  key: 'bp',
                  header: 'BP',
                  render: (r) =>
                    r.systolic == null && r.diastolic == null
                      ? '—'
                      : `${r.systolic ?? '—'}/${r.diastolic ?? '—'}`,
                },
                {
                  key: 'temp',
                  header: 'Temp',
                  render: (r) =>
                    r.skin_temp_c == null ? '—' : `${r.skin_temp_c.toFixed(1)} °C`,
                },
                { key: 'hrv', header: 'HRV', render: (r) => dash(r.hrv_ms, 'ms') },
                { key: 'stress', header: 'Stress', render: (r) => dash(r.stress, '') },
                {
                  key: 'glucose',
                  header: 'Glucose',
                  render: (r) =>
                    r.glucose_mmol == null ? '—' : `${r.glucose_mmol.toFixed(1)} mmol/L`,
                },
                {
                  key: 'sleep',
                  header: 'Sleep',
                  render: (r) =>
                    r.sleep_minutes == null
                      ? '—'
                      : `${Math.floor(r.sleep_minutes / 60)}h ${r.sleep_minutes % 60}m`,
                },
                { key: 'src', header: 'Source', render: (r) => r.source },
              ]}
            />
          </Card>
        )}
      </Loaded>
    </div>
  )
}

/** A reading, or an em dash. Nullable throughout on purpose: a sensor that was
 *  never run has no value, and printing 0 for it would be a measurement. */
function dash(value: number | null, unit: string): string {
  if (value == null) return '—'
  return unit ? `${value} ${unit}` : `${value}`
}

// ── devices ──────────────────────────────────────────────────────────────────

function DevicesTab({ user }: { user: Detail }) {
  return (
    <div className="grid grid--2">
      <Card title="Paired devices" flush>
        <Table
          rows={user.devices}
          rowKey={(row) => row.id}
          empty="No devices paired."
          columns={[
            {
              key: 'label',
              header: 'Device',
              strong: true,
              render: (r) => r.label || titleCase(r.kind),
            },
            { key: 'kind', header: 'Kind', render: (r) => <Badge>{titleCase(r.kind)}</Badge> },
            { key: 'fw', header: 'Firmware', render: (r) => <span className="mono">{r.firmware || '—'}</span> },
            {
              key: 'battery',
              header: 'Battery',
              render: (r) => (
                <span className="row" style={{ gap: 8, minWidth: 110 }}>
                  <span className="num">{r.battery}%</span>
                  <span style={{ flex: 1 }}>
                    <Meter
                      value={r.battery}
                      max={100}
                      tone={r.battery < 15 ? 'var(--alert)' : r.battery < 40 ? 'var(--orange)' : undefined}
                    />
                  </span>
                </span>
              ),
            },
            { key: 'seen', header: 'Last seen', render: (r) => <span className="dim">{relative(r.last_seen_at)}</span> },
          ]}
        />
      </Card>

      <Card title="Third-party connections" hint="OAuth grants to Whoop, Garmin, Fitbit, Oura…" flush>
        <Table
          rows={user.connections}
          rowKey={(row) => row.provider}
          empty="No third-party sources connected."
          columns={[
            { key: 'provider', header: 'Provider', strong: true, render: (r) => titleCase(r.provider) },
            {
              key: 'state',
              header: 'State',
              render: (r) =>
                !r.enabled ? (
                  <Badge>Disabled</Badge>
                ) : r.last_error ? (
                  <Badge tone="alert">Erroring</Badge>
                ) : (
                  <Badge tone="green">Connected</Badge>
                ),
            },
            { key: 'sync', header: 'Last sync', render: (r) => <span className="dim">{relative(r.last_sync_at)}</span> },
            { key: 'expiry', header: 'Token expires', render: (r) => <span className="dim">{relative(r.expires_at)}</span> },
            {
              key: 'error',
              header: 'Last error',
              render: (r) => <span className="dim" style={{ fontSize: 11 }}>{r.last_error ?? '—'}</span>,
            },
          ]}
        />
      </Card>
    </div>
  )
}

// ── emergency ────────────────────────────────────────────────────────────────

function EmergencyTab({ user }: { user: Detail }) {
  const t = user.emergency.thresholds
  return (
    <div className="grid grid--2">
      <div className="stack">
        <Card title="Contacts">
          <div className="stack">
            <Notice>
              Contact names and numbers are PII belonging to third parties who never signed up
              for this service. The console shows that dispatch has somewhere to go, not who it
              goes to.
            </Notice>
            <div className="row" style={{ gap: 24 }}>
              <span>
                <span className="kpi__label">Contacts</span>
                <div className="num" style={{ fontSize: 24 }}>
                  {user.emergency.contact_count}
                </div>
              </span>
              <span>
                <span className="kpi__label">Primary set</span>
                <div style={{ marginTop: 6 }}>
                  {user.emergency.has_primary ? (
                    <Badge tone="green">Yes</Badge>
                  ) : (
                    <Badge tone="alert">No — dispatch has nobody to call</Badge>
                  )}
                </div>
              </span>
            </div>
          </div>
        </Card>

        <Card title="Trigger thresholds" hint="Set by the person in the app; read-only here">
          {t ? (
            <dl className="stack" style={{ margin: 0, gap: 10 }}>
              {/* The clinical numbers are deliberately withheld by the API —
                  this endpoint sits behind `user:read:any` with no typed reason
                  and no PHI audit record, so a support agent opening it for a
                  billing query must not be shown someone's trigger values.
                  They were still being printed here, and `String(undefined)`
                  renders the literal text "undefined": four rows of it, plus
                  "undefined–undefined" for the heart-rate range. What the API
                  does return in their place is whether the thresholds have been
                  customised, which is the operationally useful fact. */}
              <Row
                label="Thresholds"
                value={
                  t.customised ? (
                    <Badge tone="cyan">Customised by the user</Badge>
                  ) : (
                    <Badge>On defaults</Badge>
                  )
                }
              />
              <Row label="Debounce" value={`${String(t.debounce_samples)} samples`} />
              <Row label="Cancel window" value={`${String(t.cancel_window_s)} s`} />
              <Row
                label="Auto-dispatch"
                value={t.auto_dispatch ? <Badge tone="green">On</Badge> : <Badge>Off</Badge>}
              />
            </dl>
          ) : (
            <Empty>Defaults in force — this person has not customised their thresholds.</Empty>
          )}
        </Card>
      </div>

      <Card title="Event history" flush>
        <Table
          rows={user.emergency.events}
          rowKey={(row) => row.id}
          empty="No emergency events for this account."
          columns={[
            { key: 'reason', header: 'Reason', strong: true, render: (r) => titleCase(r.reason) },
            {
              key: 'outcome',
              header: 'Outcome',
              render: (r) => (
                <Badge tone={r.outcome === 'cancelled' ? undefined : 'alert'}>
                  {titleCase(r.outcome)}
                </Badge>
              ),
            },
            { key: 'at', header: 'Triggered', render: (r) => <span className="dim">{dateTime(r.triggered_at)}</span> },
            {
              key: 'link',
              header: '',
              render: (r) => (
                <Link className="dim" to={`/emergency?event=${r.id}`}>
                  Timeline →
                </Link>
              ),
            },
          ]}
        />
      </Card>
    </div>
  )
}

// ── AI ───────────────────────────────────────────────────────────────────────

function AiTab({ user, onSaved }: { user: Detail; onSaved: () => void }) {
  const { can } = useAuth()
  const run = useAction()
  const [budget, setBudget] = useState(String(user.ai.budget_usd ?? ''))
  const [hardStop, setHardStop] = useState(user.ai.hard_stop ?? true)
  const [confirming, setConfirming] = useState(false)

  const requests = useResource(
    (signal) => get<AiRequestRow[]>('/v1/admin/ai/requests', { user_id: user.id, limit: 50 }, signal),
    [user.id],
  )

  return (
    <div className="stack">
      <div className="grid grid--kpi">
        <Kpi label="Lifetime spend" value={money(user.ai.lifetime_spend_usd)} tone="green" />
        <Kpi label="Requests" value={count(user.ai.request_count)} />
        <Kpi
          label="Monthly budget"
          value={user.ai.budget_usd === null ? 'Default' : money(user.ai.budget_usd)}
          foot={user.ai.hard_stop ? 'Hard stop at the cap' : 'Soft cap — requests continue'}
        />
      </div>

      {can(P.configWrite) && (
        <Card title="Budget" hint="Applies to this account only; org and global caps still bind">
          <div className="row row--wrap">
            <div className="field" style={{ width: 180 }}>
              <label className="field__label" htmlFor="budget">
                Monthly cap (USD)
              </label>
              <input
                id="budget"
                className="input"
                type="number"
                min={0}
                step="0.5"
                value={budget}
                onChange={(event) => setBudget(event.target.value)}
              />
            </div>
            <label className="row" style={{ gap: 8, fontSize: 12.5, alignSelf: 'flex-end', paddingBottom: 9 }}>
              <input
                type="checkbox"
                checked={hardStop}
                onChange={(event) => setHardStop(event.target.checked)}
              />
              Hard stop when reached
            </label>
            <button
              className="btn btn--sm btn--primary"
              style={{ alignSelf: 'flex-end', marginBottom: 6 }}
              disabled={budget === ''}
              onClick={() => setConfirming(true)}
            >
              Save budget
            </button>
          </div>
        </Card>
      )}

      <Card
        title="Request log"
        hint={
          can(P.aiPromptRead)
            ? 'You hold ai:prompt:read — prompt text is shown, and reading it is audited'
            : 'Prompt text is withheld: reading what a patient typed needs ai:prompt:read'
        }
        flush
      >
        <Loaded resource={requests}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(row) => row.id}
              empty="This account has not used the co-pilot."
              columns={[
                { key: 'at', header: 'When', render: (r) => <span className="dim">{dateTime(r.created_at)}</span> },
                { key: 'task', header: 'Task', strong: true, render: (r) => titleCase(r.task_class) },
                {
                  key: 'model',
                  header: 'Model',
                  render: (r) => (
                    <span>
                      <span className="mono">{r.model_id}</span>
                      {r.failed_over_from && (
                        <span className="dim" style={{ fontSize: 10.5, display: 'block' }}>
                          failed over from {r.failed_over_from}
                        </span>
                      )}
                    </span>
                  ),
                },
                { key: 'red', header: 'Redactions', render: (r) => <span className="num">{r.redactions}</span> },
                {
                  key: 'tok',
                  header: 'Tokens',
                  render: (r) => (
                    <span className="dim">
                      {tokens(r.input_tokens)} in · {tokens(r.output_tokens)} out
                    </span>
                  ),
                },
                { key: 'cost', header: 'Cost', render: (r) => <span className="num">{money(r.cost_usd)}</span> },
                { key: 'lat', header: 'Latency', render: (r) => <span className="dim">{ms(r.latency_ms)}</span> },
                {
                  key: 'state',
                  header: 'Result',
                  render: (r) =>
                    r.error ? <Badge tone="alert">Error</Badge> : <Badge tone="green">OK</Badge>,
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {confirming && (
        <ReasonDialog
          title="Set the AI budget for this account"
          confirmLabel="Save"
          onClose={() => setConfirming(false)}
          onConfirm={(reason) =>
            run(
              () =>
                post(`/v1/admin/users/${user.id}/budget`, {
                  monthly_usd: Number(budget),
                  hard_stop: hardStop,
                  reason,
                }),
              'Budget updated.',
              () => {
                setConfirming(false)
                onSaved()
              },
            )
          }
        />
      )}
    </div>
  )
}

// ── subscription ─────────────────────────────────────────────────────────────

function SubscriptionTab({ user }: { user: Detail }) {
  const s = user.subscription
  return (
    <div className="grid grid--2">
      <Card title="Entitlement">
        <dl className="stack" style={{ margin: 0, gap: 10 }}>
          <Row
            label="Tier"
            value={s.tier === 'pro' ? <Badge tone="lime">Pro</Badge> : <Badge>Free</Badge>}
          />
          <Row label="Plan" value={s.plan || '—'} />
          <Row label="Store" value={s.store ? titleCase(s.store) : '—'} />
          <Row label="Renews" value={dateTime(s.renews_at)} />
          <Row label="Trial ends" value={dateTime(s.trial_ends_at)} />
          <Row label="Cancelled" value={dateTime(s.cancelled_at)} />
        </dl>
      </Card>
      <TierCard user={user} />
    </div>
  )
}

/** Grant or revoke Pro by hand, for the cases a payment processor cannot serve. */
function TierCard({ user }: { user: Detail }) {
  const { can } = useAuth()
  const run = useAction()
  const [dialog, setDialog] = useState<'grant' | 'revoke' | null>(null)
  const isPro = user.subscription.tier === 'pro'

  if (!can(P.billingWrite)) return null

  return (
    <Card title="Granting Pro">
      <div className="stack" style={{ gap: 10 }}>
        <Notice tone="warn">
          Stripe stays the source of truth for anyone actually paying — a webhook will overwrite
          a hand-set tier, and that is correct. Use this for a support remedy, a comped account,
          or testing entitlement without putting a card through.
        </Notice>

        {isPro ? (
          <button className="btn btn--sm btn--danger" onClick={() => setDialog('revoke')}>
            Revoke Pro
          </button>
        ) : (
          <button className="btn btn--sm btn--primary" onClick={() => setDialog('grant')}>
            Grant Pro
          </button>
        )}
      </div>

      {dialog && (
        <ReasonDialog
          title={dialog === 'grant' ? 'Grant Pro to this account' : 'Revoke Pro from this account'}
          description={
            dialog === 'grant'
              ? 'Unlocks the co-pilot, coach and automatic dispatch immediately. Say why — this is a billing decision and the reason is kept.'
              : 'Removes the paid features immediately. If this person is paying through a store, the next webhook will restore it.'
          }
          confirmLabel={dialog === 'grant' ? 'Grant Pro' : 'Revoke Pro'}
          danger={dialog === 'revoke'}
          // The endpoint requires 10; the dialog defaults to 3, so without this
          // the operator types a reason and gets a 422 back.
          minLength={10}
          onClose={() => setDialog(null)}
          onConfirm={(reason) =>
            run(
              () =>
                post(`/v1/admin/users/${user.id}/subscription`, {
                  tier: dialog === 'grant' ? 'pro' : 'free',
                  reason,
                }),
              dialog === 'grant' ? 'Pro granted.' : 'Pro revoked.',
            )
          }
        />
      )}
    </Card>
  )
}

// ── audit ────────────────────────────────────────────────────────────────────

function AuditTab({ userId }: { userId: string }) {
  const rows = useResource(
    (signal) => get<AuditRow[]>('/v1/admin/audit', { target_id: userId, limit: 200 }, signal),
    [userId],
  )

  return (
    <Card title="Everything anyone did to this account" flush>
      <Loaded resource={rows}>
        {(data) => (
          <Table
            rows={data}
            rowKey={(row) => row.id}
            empty="No audit entries reference this account."
            columns={[
              { key: 'at', header: 'When', render: (r) => <span className="dim">{dateTime(r.at)}</span> },
              { key: 'action', header: 'Action', strong: true, render: (r) => <span className="mono">{r.action}</span> },
              { key: 'actor', header: 'Actor', render: (r) => <span className="mono">{r.actor_id ?? 'system'}</span> },
              { key: 'reason', header: 'Reason', render: (r) => r.reason || <span className="dim">—</span> },
              { key: 'ip', header: 'IP', render: (r) => <span className="mono dim">{r.ip || '—'}</span> },
            ]}
          />
        )}
      </Loaded>
    </Card>
  )
}

// ── roles ────────────────────────────────────────────────────────────────────

function RolesCard({
  user,
  onChanged,
  run,
}: {
  user: Detail
  onChanged: () => void
  run: ReturnType<typeof useAction>
}) {
  const [granting, setGranting] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const held = new Set(user.roles.map((r) => r.role))

  return (
    <Card title="Roles" hint="Every grant and revoke is audited with a reason">
      <div className="row row--wrap">
        {ROLE_OPTIONS.map((role) => (
          <button
            key={role}
            className={held.has(role) ? 'btn btn--sm btn--danger' : 'btn btn--sm'}
            onClick={() => (held.has(role) ? setRevoking(role) : setGranting(role))}
          >
            {held.has(role) ? `Revoke ${titleCase(role)}` : `Grant ${titleCase(role)}`}
          </button>
        ))}
      </div>

      {granting && (
        <ReasonDialog
          title={`Grant “${titleCase(granting)}”`}
          description={
            granting === 'superadmin'
              ? 'Superadmin holds every permission including PHI read and prompt read. Only a superadmin can grant it.'
              : undefined
          }
          confirmLabel="Grant role"
          onClose={() => setGranting(null)}
          onConfirm={(reason) =>
            run(
              () => post(`/v1/admin/users/${user.id}/roles`, { role: granting, reason }),
              `Granted ${granting}.`,
              onChanged,
            )
          }
        />
      )}

      {revoking && (
        <ReasonDialog
          title={`Revoke “${titleCase(revoking)}”`}
          description="The API refuses to remove the last superadmin — a console that could lock everyone out would not be recoverable."
          confirmLabel="Revoke role"
          danger
          onClose={() => setRevoking(null)}
          onConfirm={(reason) =>
            run(
              () =>
                api(`/v1/admin/users/${user.id}/roles/${revoking}`, {
                  method: 'DELETE',
                  query: { reason },
                }),
              `Revoked ${revoking}.`,
              onChanged,
            )
          }
        />
      )}
    </Card>
  )
}
